import { useCallback, useEffect, useState } from 'react';
import { useCheckoutApi } from '../Api';
import { PagedResult } from '../types/api';
import { CheckoutOrderListItemDto } from '../types/checkout';

const PAGE_SIZE = 10;

interface CheckoutOrdersState {
  orders: CheckoutOrderListItemDto[];
  totalCount: number;
  totalPages: number;
  loading: boolean;
  error: boolean;
}

/** Fetches one page of the logged-in user's checkout orders from the Checkout API. */
export function useCheckoutOrders(pageNumber: number): CheckoutOrdersState {
  const checkoutApi = useCheckoutApi();
  const [state, setState] = useState<CheckoutOrdersState>({
    orders: [],
    totalCount: 0,
    totalPages: 1,
    loading: true,
    error: false,
  });

  const load = useCallback(
    async (signal: AbortSignal) => {
      setState((s) => ({ ...s, loading: true, error: false }));
      try {
        const response = await checkoutApi.get<
          PagedResult<CheckoutOrderListItemDto>
        >('/api/checkout/orders', {
          params: { pageNumber, pageSize: PAGE_SIZE },
          signal,
        });

        setState({
          orders: response.data?.items ?? [],
          totalCount: response.data?.totalCount ?? 0,
          totalPages: response.data?.totalPages ?? 1,
          loading: false,
          error: false,
        });
      } catch {
        // Ignore aborts (unmount / effect re-run); only surface real failures.
        if (signal.aborted) return;
        setState({
          orders: [],
          totalCount: 0,
          totalPages: 1,
          loading: false,
          error: true,
        });
      }
    },
    [checkoutApi, pageNumber]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return state;
}
